import { useAuth } from "context/AuthContext";
import { TeacherCreationProvider, teacherStepLocalKey, } from "context/TeacherCreationContext";
import { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { convertHexToRGBA, defaultThemeColors, fonts, pageContainerStyles } from "ui/theme";
import { safeLocalStorage } from "utils";
import { appRoute } from "../app.routes";
import { useMediaQuery } from "react-responsive";
import { ChevronRight } from "ui/icons";
import { PrevButton } from "ui/base/Button/EmblaButtons";

export const CompleteProfileRoot = ({ children }) => {
    const { pathname } = useLocation();
    const { isLoading, userData } = useAuth();
    const navigate = useNavigate();

    const isMobile = useMediaQuery({ query: "(max-width: 768px)" });

    const [showContentMobile, setShowContentMobile] = useState(false);

    const stepHeaderOptions = {
        "complete-profile": {
            step: 1,
            title: "Class Listing",
            navigateBack: appRoute.completeProfile.fullPath,
        },
        second: {
            step: 2,
            title: "Type & Price",
            navigateBack: appRoute.completeProfileSecond.fullPath,
        },
        third: {
            step: 3,
            title: (
                <>
                    Policies
                </>
            ),
            navigateBack: appRoute.completeProfileThird.fullPath,
        },
        fourth: {
            step: 4,
            title: "Photos",
            navigateBack: appRoute.completeProfileFourth.fullPath,
        },
        fifth: {
            step: 5,
            title: "Schedule",
            navigateBack: appRoute.completeProfileFifth.fullPath,
        }
    };

    const pathSegment = pathname?.split("/").pop();
    const currentStep = stepHeaderOptions[pathSegment];
    const activeStep = currentStep?.step;

    useEffect(() => {
        const navigation = {
            1: appRoute.completeProfile.fullPath,
            2: appRoute.completeProfileSecond.fullPath,
            3: appRoute.completeProfileThird.fullPath,
            4: appRoute.completeProfileFourth.fullPath,
            5: appRoute.completeProfileFifth.fullPath,
        };
        const step = safeLocalStorage.getItem(teacherStepLocalKey);

        if (userData?.teacher?.profile_completion === "50") {
            navigate(appRoute.completeProfileFourth.fullPath)
            return
        }
        if (userData?.teacher?.profile_completion === "75") {
            navigate(appRoute.completeProfileFifth.fullPath)
            return
        }
        navigate(navigation?.[step] || appRoute.completeProfile.fullPath);
    }, [isLoading]);


    return (
        <Container>
            <div className="title">
                {showContentMobile && <MobileWrapper hide>
                    <PrevButton onClick={(() => {
                        setShowContentMobile(false)
                    })} chevronColor={"black"} />
                </MobileWrapper>}

                <Title isMobile={isMobile}>{showContentMobile ? currentStep?.title : "Class Listing"}</Title>
            </div>

            {showContentMobile && isMobile ? <div>
                <TeacherCreationProvider>
                    <Content >{children || <Outlet />}</Content>
                </TeacherCreationProvider>

            </div> : <div className="content">
                <Sidebar>
                    {Object.entries(stepHeaderOptions).map((item) => (
                        <SidebarItem
                            key={item[0]}
                            isMobile={isMobile}
                            active={activeStep === item?.[1]?.step && !isMobile}
                            disabled={activeStep < item?.[1]?.step}
                            onClick={() => {
                                if (activeStep > item?.[1]?.step) {
                                    navigate(item?.[1]?.navigateBack)
                                }
                                setShowContentMobile(true)
                            }}
                        >
                            {item?.[1]?.title}
                            <MobileWrapper hide>
                                <ChevronRight />
                            </MobileWrapper>
                        </SidebarItem>
                    ))}
                </Sidebar>
                {!isLoading && (
                    <TeacherCreationProvider>
                        <Content isMobile={isMobile}>{children || <Outlet />}</Content>
                    </TeacherCreationProvider>
                )}
            </div>}
        </Container>
    );
};

const Container = styled.div`
    ${pageContainerStyles}
    padding-bottom: 60px;
    display: flex;
    gap: 1rem;
    flex-direction: column;
    font-family: var(--font-secondary);
    overflow: visible;
    min-height: var(--page-height);
    padding-top: 60px;

    & .content {
        display: flex;
        flex-direction: row;
        gap: 8rem;
    }

    & .title {
        display: flex;
        flex-direction: row;
        gap: 1rem;
        height: fit-content;
        align-items: center;  
    }
`;

const Sidebar = styled.div`
    display: flex;
    flex-direction: column;
    top: 40px;
    height: fit-content;
    gap: 1rem;
    width: 100%;

    @media (min-width: 1024px) {
        min-width: 250px;
        width: auto;
        position: sticky;
    } 
`;

const Title = styled.h1`
    padding-bottom: ${({ isMobile }) => isMobile ? "0rem" : "1rem"};
    height: fit-content;
`;

const SidebarItem = styled.button`
    background: ${({ active }) =>
        active ? convertHexToRGBA(defaultThemeColors.blue, 0.05) : "none"};
    color: ${({ active }) =>
        active ? defaultThemeColors.blue : defaultThemeColors.txt};
    border: none;
    padding: 1rem;
    text-align: left;
    cursor: pointer;
    border-radius: 8px;
    font-family: Montserrat;
    font-size: ${fonts.sizes.regular};
    font-weight: 600;
    pointer-events: ${({ disabled }) => (disabled ? "none" : "auto")};
    cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
    opacity: ${({ disabled }) => (disabled ? "0.6" : "1")};

    &:hover {
        background-color: ${({ isMobile }) => !isMobile ? convertHexToRGBA(defaultThemeColors.blue, 0.05) : null};
        color: ${({ isMobile }) => !isMobile ? defaultThemeColors.blue : null};
    }

    justify-content: space-between;
    display: flex;
`;

const Content = styled.div`
    flex: 1; 
    flex-direction: column;
    display: ${({ isMobile }) => !isMobile ? "flex" : "none"};
`;

const MobileWrapper = styled.div`
    display: block;

    @media (min-width: 767px) {
        ${(props) =>
        props.hide &&
        `
      display: none;
    `}
    }
`;
